import React, { useState, useEffect } from 'react';
import { FileText, Download, Check, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from '../translations';
import {
  useFloating,
  useInteractions,
  useDismiss,
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal
} from '@floating-ui/react';
import { Viewer, Worker, SpecialZoomLevel } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import { Button } from '@/components/ui/button';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  jobTitle: string;
}

interface WhitepaperCTAProps {
  className?: string;
}

const WHITEPAPER_URL = "https://guurrickmxxnxobbsjbp.supabase.co/storage/v1/object/public/whitepaper//Whitepaper%20MRI%20Project.pdf";

export function WhitepaperCTA({ className }: WhitepaperCTAProps) {
  const { t, lang } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isSliding, setIsSliding] = useState(false);

  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  const { refs, context } = useFloating({
    open: isModalOpen,
    onOpenChange: setIsModalOpen
  });

  const dismiss = useDismiss(context);
  const { getFloatingProps } = useInteractions([dismiss]);

  const handlePageChange = (newPage: number) => {
    setIsSliding(true);
    setCurrentPage(newPage);
    setTimeout(() => setIsSliding(false), 300);
  };

  // Translations for the whitepaper title and subtitle
  const whitepaper = {
    NL: {
      title: "BizComfort Whitepaper",
      subtitle: "Leer over onze innovatieve aanpak voor technisch beheer"
    },
    FR: {
      title: "BizComfort Whitepaper",
      subtitle: "Découvrez notre approche innovante de la gestion technique"
    },
    EN: {
      title: "BizComfort Whitepaper",
      subtitle: "Learn about our innovative approach to technical management"
    }
  };

  return (
    <>
      <Button
        onClick={() => setIsModalOpen(true)}
        size="lg"
        className={`gap-2 bg-white text-gray-900 hover:bg-gray-100 border-0 rounded-full px-6 py-3 h-auto ${className}`}
        variant="outline"
      >
        <FileText className="w-5 h-5" />
        Download Whitepaper
      </Button>

      <FloatingPortal>
        {isModalOpen && (
          <FloatingOverlay
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 grid place-items-center p-4"
            lockScroll
          >
            <FloatingFocusManager context={context}>
              <div
                ref={refs.setFloating}
                {...getFloatingProps()}
                className="bg-white rounded-2xl w-full max-w-6xl shadow-2xl"
              >
                <div className="p-6 md:p-8 flex justify-between items-start">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      {whitepaper[lang].title}
                    </h2>
                    <p className="text-gray-600">
                      {whitepaper[lang].subtitle}
                    </p>
                  </div>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {/* Coming Soon Banner */}
                <div className="relative overflow-hidden">
                  <div className="absolute inset-0 bg-black/80 backdrop-blur-sm z-10 flex items-center justify-center">
                    <div className="text-center">
                      <h3 className="text-3xl font-bold text-white mb-2">Coming Soon</h3>
                      <p className="text-white/80">Our whitepaper is currently under construction</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-8 opacity-20">
                    {/* Preview Section */}
                    <div className="p-6 md:p-8 border-t md:border-t-0 md:border-r border-gray-100">
                      <div className="relative bg-gray-100 rounded-lg overflow-hidden" style={{ height: '600px' }}>
                        <div className={`h-full transition-transform duration-300 ease-in-out transform ${
                          isSliding ? 'scale-95 opacity-50' : 'scale-100 opacity-100'
                        }`}>
                          <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
                            <Viewer
                              fileUrl={WHITEPAPER_URL}
                              defaultScale={SpecialZoomLevel.PageFit}
                              onDocumentLoad={({ doc }) => setTotalPages(doc.numPages)}
                              initialPage={currentPage}
                              onPageChange={(e) => setCurrentPage(e.currentPage)}
                            />
                          </Worker>
                        </div>
                        
                        {/* Navigation Controls */}
                        <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/50 to-transparent">
                          <div className="flex items-center justify-between text-white">
                            <button
                              onClick={() => handlePageChange(Math.max(0, currentPage - 1))}
                              disabled={currentPage === 0 || isSliding}
                              className="p-2 rounded-full bg-black/20 hover:bg-black/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <ChevronLeft className="h-6 w-6" />
                            </button>
                            <span className="text-sm font-medium bg-black/20 px-3 py-1 rounded-full">
                              Page {currentPage + 1} of {totalPages}
                            </span>
                            <button
                              onClick={() => handlePageChange(Math.min(totalPages - 1, currentPage + 1))}
                              disabled={currentPage === totalPages - 1 || isSliding}
                              className="p-2 rounded-full bg-black/20 hover:bg-black/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <ChevronRight className="h-6 w-6" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Form Section */}
                    <div className="p-6 md:p-8 border-t md:border-t-0 border-gray-100">
                      <form className="space-y-6">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                              First Name *
                            </label>
                            <input
                              type="text"
                              id="firstName"
                              required
                              disabled
                              className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-jaimy-red focus:border-transparent transition-shadow bg-gray-50 cursor-not-allowed"
                            />
                          </div>
                          <div>
                            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                              Last Name *
                            </label>
                            <input
                              type="text"
                              id="lastName"
                              required
                              disabled
                              className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-jaimy-red focus:border-transparent transition-shadow bg-gray-50 cursor-not-allowed"
                            />
                          </div>
                        </div>

                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                            Business Email *
                          </label>
                          <input
                            type="email"
                            id="email"
                            required
                            disabled
                            className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-jaimy-red focus:border-transparent transition-shadow bg-gray-50 cursor-not-allowed"
                          />
                        </div>

                        <div>
                          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                            Phone Number *
                          </label>
                          <input
                            type="tel"
                            id="phone"
                            required
                            disabled
                            className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-jaimy-red focus:border-transparent transition-shadow bg-gray-50 cursor-not-allowed"
                          />
                        </div>

                        <div>
                          <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                            Company Name *
                          </label>
                          <input
                            type="text"
                            id="company"
                            required
                            disabled
                            className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-jaimy-red focus:border-transparent transition-shadow bg-gray-50 cursor-not-allowed"
                          />
                        </div>

                        <div>
                          <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700 mb-1">
                            Job Title *
                          </label>
                          <input
                            type="text"
                            id="jobTitle"
                            required
                            disabled
                            className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-jaimy-red focus:border-transparent transition-shadow bg-gray-50 cursor-not-allowed"
                          />
                        </div>

                        <button
                          type="button"
                          disabled
                          className="w-full gradient-bg text-white py-3 rounded-lg font-medium opacity-50 cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          <Download className="h-5 w-5" />
                          Coming Soon
                        </button>

                        <p className="text-xs text-gray-500 text-center">
                          By downloading this whitepaper, you agree to receive communications from us. You can unsubscribe at any time.
                        </p>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </FloatingFocusManager>
          </FloatingOverlay>
        )}
      </FloatingPortal>
    </>
  );
}